import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNfpReg } from '../../../contexts/NfpRegContext';
import { nfpApi } from '../../../services/nfpApi';
import { ROLE_LABELS } from './roleOptions';

const PEACH = '#EBA477';
const GREEN = '#10B981';
const AGREEMENT_VERSION = '2026-01-15';
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

function getRecognitionLabel(type) {
  switch (type) {
    case 'irs_determination_letter':
      return 'IRS determination letter';
    case 'parent_group_exemption':
      return 'Covered under a parent organization’s group exemption / group ruling';
    case 'religious_no_separate_letter':
      return 'Church or religious organization without a separate IRS determination letter';
    case 'government_unit_public_entity':
      return 'Government unit / public entity';
    case 'other_not_sure':
      return 'Other / Not sure';
    default:
      return '—';
  }
}

function deriveProofType(recognitionType, isCoveredByParentGroupExemption) {
  switch (recognitionType) {
    case 'irs_determination_letter':
      return 'lod';
    case 'parent_group_exemption':
      return 'operating_under_gen';
    case 'religious_no_separate_letter':
      return isCoveredByParentGroupExemption === 'yes' ? 'operating_under_gen' : 'other_supporting';
    case 'government_unit_public_entity':
      return 'government_unit';
    case 'other_not_sure':
      return 'other_supporting';
    default:
      return 'lod';
  }
}

function getProofUploadCopy(recognitionType, isCoveredByParentGroupExemption) {
  const fallback =
    'If you do not have the exact document listed, upload the closest supporting document available. Our team will review it.';

  switch (recognitionType) {
    case 'irs_determination_letter':
      return {
        sectionHelper:
          'Upload the document(s) that best support how this organization’s tax status is recognized. If you do not have the exact document listed, upload the closest supporting document available. Our team will review it.',
        rows: {
          letterOfDetermination: {
            label: 'IRS Determination Letter',
            helper: 'Upload the organization’s IRS determination letter confirming its tax-exempt status.',
            fallback,
          },
        },
      };
    case 'parent_group_exemption':
      return {
        sectionHelper:
          'Upload two documents: one showing the parent organization’s tax-exempt status or group exemption, and one showing that this local unit is included, recognized, or covered by the parent organization.',
        rows: {
          operatingUnderGenDoc: {
            label: 'Parent Organization Exemption Document',
            helper: 'Upload a document showing the parent organization’s tax-exempt status or group exemption.',
            fallback,
          },
          chapterAuthorization: {
            label: 'Proof of Local Unit Recognition',
            helper: 'Upload a document showing this local unit is included, recognized, or covered by the parent organization.',
            fallback,
          },
        },
      };
    case 'religious_no_separate_letter':
      if (isCoveredByParentGroupExemption === 'yes') {
        return {
          sectionHelper:
            'Upload two documents: one showing the parent organization’s tax-exempt status or group exemption, and one showing that this church, parish, ministry, or local unit is covered by or recognized by the parent organization.',
          rows: {
            operatingUnderGenDoc: {
              label: 'Parent Organization Exemption Document',
              helper: 'Upload a document showing the parent organization’s tax-exempt status or group exemption.',
              fallback,
            },
            chapterAuthorization: {
              label: 'Proof of Local Unit Recognition',
              helper: 'Upload a document showing this church, parish, ministry, or local unit is covered by or recognized by the parent organization.',
              fallback,
            },
          },
        };
      }
      return {
        sectionHelper:
          'Upload one document that best supports the organization’s religious status and tax-exempt status.',
        rows: {
          otherSupportingDoc: {
            label: 'Religious Status Supporting Document',
            helper: 'Upload the best document available to support the organization’s religious status and tax-exempt status.',
            fallback,
          },
        },
      };
    case 'government_unit_public_entity':
      return {
        sectionHelper:
          'Upload one document that confirms this organization is a government unit, public entity, or part of one.',
        rows: {
          governmentUnitDoc: {
            label: 'Government / Public Entity Document',
            helper: 'Upload a document that confirms this organization is a government unit, public entity, or part of one.',
            fallback,
          },
        },
      };
    case 'other_not_sure':
      return {
        sectionHelper:
          'Upload one supporting document that best helps explain the organization’s tax status, affiliation, or tax-exempt status.',
        rows: {
          otherSupportingDoc: {
            label: 'Supporting Document',
            helper: 'If you are unsure, upload the best document you have and our team will review it.',
            fallback,
          },
        },
      };
    default:
      return {
        sectionHelper:
          'Upload the document(s) that best support how this organization’s tax status is recognized. If you do not have the exact document listed, upload the closest supporting document available. Our team will review it.',
        rows: {},
      };
  }
}

const FrostedInput = ({ style, ...props }) => (
  <input
    {...props}
    style={{
      width: '100%',
      height: 56,
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.10)',
      background: 'rgba(255,255,255,0.12)',
      color: 'white',
      fontSize: 16,
      padding: '0 14px',
      boxSizing: 'border-box',
      outline: 'none',
      ...style,
    }}
  />
);

export default function Step4NfpAgreements({ onBack, onNext }) {
  const { draft, setDraft, registration, setRegistration } = useNfpReg();

  const account = useMemo(() => draft?.account || {}, [draft?.account]);
  const regId =
    registration?._id ||
    registration?.id ||
    draft?.registration?._id ||
    draft?.registration?.id ||
    null;

  // Derived from Step 1 (no in‑step toggling)
  const isSignatory = !(account.isSignatory === false || account.isSignatory === 'no');

  // Registrar defaults (person doing enrollment)
  const registrarName = (account.fullName || account.name || '').trim();
const registrarTitle = ROLE_LABELS[account.role] || account.role || 'Registrar';
  const registrarEmail = (account.email || '').trim();

  // Signatory defaults (captured in Step 1 for non‑signatories)
  const signatoryDefaults = useMemo(
    () => ({
      name: (account?.inviteSignatory?.name || registrarName || '').trim(),
      title: (account?.inviteSignatory?.title || 'Authorized Signer').trim(),
      email: (account?.inviteSignatory?.email || '').trim(),
    }),
    [account?.inviteSignatory, registrarName]
  );
  const [signatory, setSignatory] = useState(signatoryDefaults);
  useEffect(() => setSignatory(signatoryDefaults), [signatoryDefaults]);

  // Structure / Proofs
  const structure = draft?.entity?.structure || 'Independent';
  const recognitionType = draft?.entity?.recognitionType || '';
  const proofChoiceLabel = getRecognitionLabel(recognitionType);
  const proofType = deriveProofType(
    recognitionType,
    draft?.entity?.isCoveredByParentGroupExemption
  );
  const proofUploadCopy = useMemo(
    () =>
      getProofUploadCopy(
        recognitionType,
        draft?.entity?.isCoveredByParentGroupExemption
      ),
    [recognitionType, draft?.entity?.isCoveredByParentGroupExemption]
  );
  const needsLetter = proofType === 'lod';
  const needsGenDoc = proofType === 'operating_under_gen';
  const needsGovDoc = proofType === 'government_unit';
  const needsOtherSupportingDoc = proofType === 'other_supporting';
  const needsChapterAuth = proofType === 'operating_under_gen';

  const addenda = useMemo(() => {
    const out = [];
    if (structure === 'Chapter/Affiliate') out.push('Chapter Authorization Addendum');
    if (structure === 'Umbrella/Parent' || draft?.entity?.scope === 'Parent Network (Full Rollout)') {
      out.push('Parent/Network Schedule');
    }
    return out;
  }, [structure, draft?.entity?.scope]);

  const existingProof = registration?.proof || draft?.proof || {};
  const [letterOfDetermination, setLetterOfDetermination] = useState(existingProof.letterOfDetermination || null);
  const [operatingUnderGenDoc, setOperatingUnderGenDoc] = useState(existingProof.operatingUnderGenDoc || null);
  const [governmentUnitDoc, setGovernmentUnitDoc] = useState(existingProof.governmentUnitDoc || null);
  const [chapterAuthorization, setChapterAuthorization] = useState(existingProof.chapterAuthorization || null);
  const [otherSupportingDoc, setOtherSupportingDoc] = useState(existingProof.otherSupportingDoc || null);

  // Terms
  const [termsHtml, setTermsHtml] = useState('');
  const termsRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = API_BASE ? `${API_BASE}/api/legal/terms?format=html&version=${AGREEMENT_VERSION}` : null;
        if (!url) throw new Error('inline');
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('bad');
        const html = await res.text();
        if (!cancelled) setTermsHtml(html || defaultInlineTerms(AGREEMENT_VERSION));
      } catch {
        if (!cancelled) setTermsHtml(defaultInlineTerms(AGREEMENT_VERSION));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const el = termsRef.current; if (!el) return;
    const check = () => {
      const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
      if (atEnd) setScrolledToEnd(true);
    };
    setTimeout(check, 0);
    const onScroll = () => check();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [termsHtml]);

  // E‑signature (hydrate from existing acceptance if present)
  const existingEnroll = draft?.agreements?.enrollmentAcceptance || {};
  const existingSignAcc = draft?.agreements?.signatoryAcceptance || {};
  const initialESign = existingEnroll?.eSign || existingSignAcc?.eSign || {};

  const [sigMode, setSigMode] = useState(initialESign.signatureMode || 'type');
  const [typedSig, setTypedSig] = useState(initialESign.typedSignature || '');
  const [drawnSig, setDrawnSig] = useState(initialESign.signatureDataUrl || '');
  const signatureReady = useMemo(() => (sigMode === 'draw' ? Boolean(drawnSig) : Boolean(typedSig.trim())), [sigMode, drawnSig, typedSig]);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    if (sigMode !== 'draw') return;
    const canvas = canvasRef.current; if (!canvas) return;
    const cssW = 520, cssH = 180, scale = window.devicePixelRatio || 1;
    canvas.width = Math.floor(cssW * scale); canvas.height = Math.floor(cssH * scale);
    canvas.style.width = `${cssW}px`; canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = 'rgba(255,255,255,0.04)'; ctx.fillRect(0, 0, cssW, cssH);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.strokeRect(0.5, 0.5, cssW - 1, cssH - 1);
  }, [sigMode]);
  const onPointerDown = (e) => { if (sigMode !== 'draw') return; drawing.current = true; const c = canvasRef.current, ctx = c.getContext('2d'), r = c.getBoundingClientRect(); ctx.beginPath(); ctx.moveTo(e.clientX - r.left, e.clientY - r.top); };
  const onPointerMove = (e) => { if (!drawing.current || sigMode !== 'draw') return; const c = canvasRef.current, ctx = c.getContext('2d'), r = c.getBoundingClientRect(); ctx.lineWidth = 2.2; ctx.strokeStyle = '#ffffff'; ctx.lineTo(e.clientX - r.left, e.clientY - r.top); ctx.stroke(); };
  const onPointerUp = () => { if (sigMode !== 'draw') return; drawing.current = false; const c = canvasRef.current; if (c) setDrawnSig(c.toDataURL('image/png')); };
  const clearCanvas = () => { const c = canvasRef.current; if (!c) return; const ctx = c.getContext('2d'); const scale = window.devicePixelRatio || 1; const w = c.width/scale, h = c.height/scale; ctx.clearRect(0, 0, w, h); ctx.fillStyle = 'rgba(255,255,255,0.04)'; ctx.fillRect(0, 0, w, h); ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.strokeRect(0.5, 0.5, w - 1, h - 1); setDrawnSig(''); };

  // Enrollment acceptance state
  const [ackChecked, setAckChecked] = useState(false);
  const [enrollmentAccepted, setEnrollmentAccepted] = useState(Boolean(existingEnroll?.acceptedAt));
  const [acceptedAt, setAcceptedAt] = useState(existingEnroll?.acceptedAt || null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  // ---------- helpers ----------
  const saveProof = async (nextFields) => {
    if (!regId) return;
    const payload = {
      type: proofType,
      letterOfDetermination,
      operatingUnderGenDoc,
      governmentUnitDoc,
      chapterAuthorization,
      otherSupportingDoc,
      ...nextFields,
    };
    const resp = await nfpApi.updateProof(regId, payload);
    if (resp?.registration) setRegistration(resp.registration);
    setDraft((prev) => ({ ...prev, proof: payload }));
  };

  const uploadFileToField = (setter, fieldName) => async (file) => {
    if (!file) return; setErr('');
    try {
      const up = await nfpApi.uploadAsset(file, { kind: 'proof', folder: 'nfp-proof' });
      const fileRef = { url: up?.url || up?.location || '', name: up?.name || file.name, mime: up?.mime || file.type, size: up?.size || file.size };
      setter(fileRef); await saveProof({ [fieldName]: fileRef }); setOk('Proof saved.');
    } catch (e) { setErr(e?.message || 'Upload failed.'); }
  };
  const removeField = (setter, fieldName) => async () => { setter(null); await saveProof({ [fieldName]: null }); };

  // Persist signatory edits to draft (+ optional applicant patch)
  const handleSignatoryChange = (field, value) => {
    const next = { ...signatory, [field]: value };
    setSignatory(next);
    setDraft((prev) => ({ ...prev, account: { ...(prev?.account || {}), inviteSignatory: next } }));
  };

  // ---------- Accept Enrollment (and Signatory if applicable) ----------
  const acceptEnrollment = async () => {
    if (!regId) return; setErr(''); setOk('');
    if (!scrolledToEnd || !ackChecked || !signatureReady || !registrarName || !registrarEmail) {
      setErr('Please review the terms, check the box, and add your signature.');
      return;
    }
    setBusy(true);
    try {
      const nowIso = new Date().toISOString();
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const eSign = {
        signatureMode: sigMode,
        typedSignature: sigMode === 'type' ? typedSig.trim() : '',
        signatureDataUrl: sigMode === 'draw' ? drawnSig : '',
        accepted: true,
      };

      const payload = {
        termsVersion: AGREEMENT_VERSION,
        enrollmentAcceptance: {
          acceptedAt: nowIso,
          accepter: { name: registrarName, title: registrarTitle, email: registrarEmail },
          eSign,
          userAgent,
        },
      };
      if (isSignatory) {
        payload.signatoryAcceptance = {
          acceptedAt: nowIso,
          signer: { name: registrarName, title: registrarTitle, email: registrarEmail },
          eSign,
          userAgent,
        };
      }

      const resp = await nfpApi.updateAgreements(regId, payload).catch(() => null);
      if (resp?.registration) setRegistration(resp.registration);

      setDraft((prev) => ({
        ...prev,
        agreements: {
          ...(prev?.agreements || {}),
          termsVersion: AGREEMENT_VERSION,
          enrollmentAcceptance: { acceptedAt: nowIso, accepter: { name: registrarName, title: registrarTitle, email: registrarEmail }, eSign, userAgent },
          ...(isSignatory
            ? { signatoryAcceptance: { acceptedAt: nowIso, signer: { name: registrarName, title: registrarTitle, email: registrarEmail }, eSign, userAgent } }
            : {}),
        },
      }));

      setEnrollmentAccepted(true); setAcceptedAt(nowIso);
      setOk(isSignatory ? 'Terms accepted (enrollment + signatory).' : 'Enrollment acknowledgment accepted.');
    } catch (e) {
      setErr(e?.message || 'Failed to accept.');
    } finally { setBusy(false); }
  };

  // Gating: Continue requires enrollment acceptance; proof can be completed later in the dashboard.
  const canContinue = useMemo(() => Boolean(regId && enrollmentAccepted && !busy), [regId, enrollmentAccepted, busy]);

  const onContinue = async () => {
    setErr(''); setOk('');
    if (!isSignatory && regId) {
      try {
        const applicantResp = await nfpApi.putApplicant(regId, { isSignatory: false, signatoryInvite: signatory });
        if (applicantResp?.registration) {
          setRegistration(applicantResp.registration);
        }
      } catch {
        // Keep step progression non-blocking for draft invite details.
      }
    }
    onNext();
  };

  return (
    <div style={{ color: 'white', display: 'grid', gap: 24 }}>
      {/* PROOF & COMPLIANCE */}
      <section style={styles.card}>
        <h2 style={styles.subheading}>Proof &amp; Compliance Uploads</h2>
        <p style={styles.note}>
          Selected in Step 2: <b>{proofChoiceLabel}</b>.
        </p>
        <p style={styles.sectionHelper}>{proofUploadCopy.sectionHelper}</p>
        <div style={styles.optionalCallout}>
          These uploads are optional during registration. You can upload or finish them later from
          <span style={{ fontWeight: 600 }}> Verification &amp; Documents</span> in the dashboard.
        </div>

        {needsLetter && (
          <UploadRow
            label={proofUploadCopy.rows.letterOfDetermination?.label || 'IRS Determination Letter'}
            helperText={proofUploadCopy.rows.letterOfDetermination?.helper}
            fallbackText={proofUploadCopy.rows.letterOfDetermination?.fallback}
            fileRef={letterOfDetermination}
            onChoose={(f) => uploadFileToField(setLetterOfDetermination, 'letterOfDetermination')(f)}
            onRemove={removeField(setLetterOfDetermination, 'letterOfDetermination')}
          />
        )}
        {needsGenDoc && (
          <UploadRow
            label={proofUploadCopy.rows.operatingUnderGenDoc?.label || 'Parent Organization Exemption Document'}
            helperText={proofUploadCopy.rows.operatingUnderGenDoc?.helper}
            fallbackText={proofUploadCopy.rows.operatingUnderGenDoc?.fallback}
            fileRef={operatingUnderGenDoc}
            onChoose={(f) => uploadFileToField(setOperatingUnderGenDoc, 'operatingUnderGenDoc')(f)}
            onRemove={removeField(setOperatingUnderGenDoc, 'operatingUnderGenDoc')}
          />
        )}
        {needsGovDoc && (
          <UploadRow
            label={proofUploadCopy.rows.governmentUnitDoc?.label || 'Government / Public Entity Document'}
            helperText={proofUploadCopy.rows.governmentUnitDoc?.helper}
            fallbackText={proofUploadCopy.rows.governmentUnitDoc?.fallback}
            fileRef={governmentUnitDoc}
            onChoose={(f) => uploadFileToField(setGovernmentUnitDoc, 'governmentUnitDoc')(f)}
            onRemove={removeField(setGovernmentUnitDoc, 'governmentUnitDoc')}
          />
        )}
        {needsChapterAuth && (
          <UploadRow
            label={proofUploadCopy.rows.chapterAuthorization?.label || 'Proof of Local Unit Recognition'}
            helperText={proofUploadCopy.rows.chapterAuthorization?.helper}
            fallbackText={proofUploadCopy.rows.chapterAuthorization?.fallback}
            fileRef={chapterAuthorization}
            onChoose={(f) => uploadFileToField(setChapterAuthorization, 'chapterAuthorization')(f)}
            onRemove={removeField(setChapterAuthorization, 'chapterAuthorization')}
          />
        )}
        {needsOtherSupportingDoc && (
          <UploadRow
            label={proofUploadCopy.rows.otherSupportingDoc?.label || 'Supporting Document'}
            helperText={proofUploadCopy.rows.otherSupportingDoc?.helper}
            fallbackText={proofUploadCopy.rows.otherSupportingDoc?.fallback}
            fileRef={otherSupportingDoc}
            onChoose={(f) => uploadFileToField(setOtherSupportingDoc, 'otherSupportingDoc')(f)}
            onRemove={removeField(setOtherSupportingDoc, 'otherSupportingDoc')}
          />
        )}

      </section>

      {/* TERMS */}
      <section>
        <h2 style={styles.heading}>Master Terms &amp; Conditions</h2>
        <div ref={termsRef} role="region" aria-label="Master Terms" tabIndex={0} style={styles.termsBox} dangerouslySetInnerHTML={{ __html: termsHtml }} />
        {addenda.length > 0 && (
          <div style={styles.badgeRow}>
            {addenda.map((doc) => (
              <span key={doc} style={styles.badge}>{doc}</span>
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
          {scrolledToEnd ? 'Thanks for reviewing the terms.' : 'Scroll through the terms to enable acceptance.'}
        </div>
      </section>

      {/* ENROLLMENT ACKNOWLEDGMENT — always shown */}
      <section style={styles.card}>
        <h3 style={styles.subheading}>Enrollment Acknowledgment</h3>
        <p style={styles.note}>
          This acknowledges that you agree to follow <span className="rypil-word">rypil</span>’s Master Terms during onboarding. If you are the
          Authorized Signatory, this will also bind the organization.
        </p>

        <div style={styles.gridThree}>
          <FrostedInput placeholder="Your full name" value={registrarName} readOnly />
          <FrostedInput placeholder="Your title" value={registrarTitle} readOnly />
          <FrostedInput placeholder="Your email" type="email" value={registrarEmail} readOnly />
        </div>

        <label style={{ ...styles.checkboxRow, opacity: scrolledToEnd ? 1 : 0.6 }}>
          <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} style={styles.checkbox} disabled={!scrolledToEnd} />
          <span>I acknowledge the Master Terms (v{AGREEMENT_VERSION}). This is my electronic signature.</span>
        </label>

        <div style={styles.signatureHeader}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setSigMode('draw')} style={sigMode === 'draw' ? tabActive : tab}>Draw</button>
            <button type="button" onClick={() => setSigMode('type')} style={sigMode === 'type' ? tabActive : tab}>Type</button>
          </div>
          <div style={{ opacity: 0.8 }}>Date: {new Date().toISOString().slice(0, 10)}</div>
        </div>

        {sigMode === 'draw' ? (
          <div style={{ marginTop: 12 }}>
            <canvas ref={canvasRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} style={styles.canvas} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={clearCanvas} style={smallBtn}>Clear</button>
            </div>
            {drawnSig ? <div style={styles.sigStatus}>Signature captured ✓</div> : null}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <FrostedInput placeholder="Type your full name as your signature" value={typedSig} onChange={(e) => setTypedSig(e.target.value)} />
            <div style={styles.sigStatus}>Your typed name will be stored as your digital signature.</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="primary"
            onClick={acceptEnrollment}
            disabled={busy || enrollmentAccepted || !ackChecked || !signatureReady || !scrolledToEnd}
            style={enrollmentAccepted ? styles.completedButton : undefined}
          >
            {enrollmentAccepted ? 'Acknowledged' : isSignatory ? 'Accept & Sign for Organization' : 'Accept Acknowledgment'}
          </button>
          {enrollmentAccepted && (
            <span style={styles.acceptedPill}>Accepted v{AGREEMENT_VERSION}{acceptedAt ? ` • ${new Date(acceptedAt).toISOString().slice(0, 10)}` : ''}</span>
          )}
        </div>
      </section>

      {/* SIGNATORY PANEL — only when registrar is NOT signatory */}
      {!isSignatory && (
        <section style={styles.card}>
          <h3 style={styles.subheading}>Authorized Signatory on File</h3>
          <div style={styles.gridThree}>
            <FrostedInput placeholder="Signer full name" value={signatory.name} onChange={(e) => handleSignatoryChange('name', e.target.value)} />
            <FrostedInput placeholder="Title" value={signatory.title} onChange={(e) => handleSignatoryChange('title', e.target.value)} />
            <FrostedInput placeholder="Email" type="email" value={signatory.email} onChange={(e) => handleSignatoryChange('email', e.target.value)} />
          </div>
        </section>
      )}

      {isSignatory && !enrollmentAccepted && (
        <div style={{ ...styles.alert, borderColor: 'rgba(248,113,113,0.45)', color: '#FCA5A5' }}>
          Acceptance required: scroll the terms, check the box, add your signature, then click “{isSignatory ? 'Accept & Sign for Organization' : 'Accept Acknowledgment'}”.
        </div>
      )}

      {err && <div style={{ ...styles.alert, borderColor: 'rgba(248,113,113,0.45)', color: '#FCA5A5' }}>{err}</div>}
      {ok && <div style={{ ...styles.alert, borderColor: 'rgba(16,185,129,0.45)', color: '#6EE7B7' }}>{ok}</div>}

      <div style={styles.actions}>
        <button type="button" className="secondary" onClick={onBack} disabled={busy}>Back</button>
        <button type="button" className="primary" onClick={onContinue} disabled={!canContinue}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

/* -------------------- Reusable Upload Row -------------------- */
function UploadRow({ label, helperText, fallbackText, fileRef, onChoose, onRemove }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { await onChoose(f); } finally { setBusy(false); e.target.value=''; } };
  return (
    <div style={styles.uploadRow}>
      <div style={{ flex: '1 1 auto' }}>
        <div style={styles.detailLabel}>{label}</div>
        {helperText ? <div style={styles.uploadHelper}>{helperText}</div> : null}
        {fileRef ? (
          <div style={styles.bodyText}><span style={{ fontWeight: 600 }}>{fileRef.name || 'Uploaded file'}</span>{fileRef.size ? ` — ${(fileRef.size / 1024).toFixed(0)} KB` : ''}</div>
        ) : (
          <div style={styles.emptyValue}>No file uploaded</div>
        )}
        {fallbackText ? <div style={styles.uploadFallback}>{fallbackText}</div> : null}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={smallBtn}>{fileRef ? 'Replace' : 'Upload'}<input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onFile} disabled={busy} /></label>
        {fileRef && (<button type="button" onClick={onRemove} style={styles.removeBtn} disabled={busy}>Remove</button>)}
      </div>
    </div>
  );
}

/* -------------------- Styles -------------------- */
const styles = {
  heading: { margin: 0, fontSize: 22, fontWeight: 700 },
  subheading: { margin: 0, fontSize: 18, fontWeight: 600 },
  termsBox: {
    maxHeight: 420,
    overflowY: 'auto',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)',
    padding: 16,
    lineHeight: 1.55,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  card: { display: 'grid', gap: 16, borderRadius: 14, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', padding: 18 },
  gridThree: { display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' },
  checkboxRow: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 },
  checkbox: { width: 18, height: 18, accentColor: PEACH, marginTop: 3 },
  signatureHeader: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' },
  canvas: { borderRadius: 12, border: '1px solid rgba(255,255,255,0.25)', marginTop: 10, touchAction: 'none', cursor: 'crosshair' },
  sigStatus: { marginTop: 8, fontSize: 13, opacity: 0.85 },
  detailLabel: { fontWeight: 600, marginRight: 6 },
  note: { fontSize: 12, opacity: 0.72 },
  sectionHelper: { margin: '-6px 0 4px', fontSize: 13, opacity: 0.86, lineHeight: 1.5 },
  optionalCallout: {
    marginTop: -2,
    borderRadius: 12,
    border: '1px solid rgba(235,164,119,0.28)',
    background: 'rgba(235,164,119,0.10)',
    padding: '10px 12px',
    fontSize: 13,
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.92)',
  },
  alert: { borderRadius: 12, border: '1px solid', padding: '10px 14px', fontSize: 13 },
  actions: { display: 'flex', justifyContent: 'space-between', paddingTop: 8 },
  badgeRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  badge: { padding: '4px 10px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', fontSize: 12 },
  uploadRow: { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.04)' },
  emptyValue: { opacity: 0.5, fontStyle: 'italic' },
  bodyText: { fontSize: 13, opacity: 0.85 },
  uploadHelper: { marginTop: 4, fontSize: 13, opacity: 0.82, lineHeight: 1.5 },
  uploadFallback: { marginTop: 6, fontSize: 12, opacity: 0.68, lineHeight: 1.45 },
  removeBtn: { border: '1px solid rgba(248,113,113,0.4)', background: 'transparent', color: '#FCA5A5', borderRadius: 10, padding: '6px 12px', cursor: 'pointer' },
  completedButton: {
    background: GREEN,
    border: `1px solid ${GREEN}`,
    color: 'white',
    opacity: 1,
    cursor: 'default',
  },
  acceptedPill: { fontSize: 12, padding: '4px 10px', borderRadius: 999, border: `1px solid ${GREEN}`, background: 'rgba(16,185,129,0.18)', color: 'white' },
};

const tab = { background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.20)', color: 'white', borderRadius: 10, padding: '8px 12px', cursor: 'pointer' };
const tabActive = { ...tab, borderColor: PEACH, background: 'rgba(235,164,119,0.25)' };
const smallBtn = { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.24)', color: 'white', borderRadius: 10, padding: '6px 12px', cursor: 'pointer' };

function stylizeRypilHtml(html) {
  return String(html || '').replace(/\brypil\b/gi, '<span class="rypil-word">rypil</span>');
}

/* --------- Full inline Terms fallback (scrollable HTML) ---------- */
function defaultInlineTerms(version) {
  return stylizeRypilHtml(`
  <div style="font-weight:700;font-size:18px;margin-bottom:6px">rypil Business and Nonprofit Terms Conditions and Commercial Co-Venture Agreement</div>
  <div style="opacity:0.9;margin-bottom:10px">
    <div><strong>Version:</strong> 1</div>
    <div><strong>Updated:</strong> January 15, 2026</div>
    <div style="margin-top:8px">These Terms and Conditions (“Terms”) govern participation by businesses (“Business” or “Partner”) and nonprofits (“Nonprofits” or “NFPs”) in the rypil platform (“Platform”), operated by rypil Technologies, Inc. (“rypil,” “we,” “us,” or “our”). By registering as a rypil business or rypil nonprofit partner, or by continuing to participate in the rypil platform, you agree to these Terms.</div>
  </div>
  <hr style="border-color:rgba(255,255,255,0.18);margin:12px 0"/>

  <h4 style="margin:8px 0">1. Definitions</h4>
  <div style="margin-bottom:10px">
    <p><strong>“Contribution”</strong> means the amount funded by a business to a nonprofit through rypil as a result of consumer transactions.</p>
    <p><strong>“Donation”</strong> and <strong>“Contribution”</strong> are used interchangeably to describe payments made by a business to a nonprofit beneficiary.</p>
    <p><strong>“Platform”</strong> means the rypil technology ecosystem, including rypil.com, rypil4biz.com, mobile applications, and related services.</p>
    <p><strong>“Registered User”</strong> means a consumer who has created a rypil account and selected a nonprofit beneficiary within the platform.</p>
  </div>

  <h4 style="margin:8px 0">2. Participation and Eligibility</h4>
  <div style="margin-bottom:10px">
    <p><strong>2.1 Eligibility Requirements.</strong> Businesses must maintain an active account in good standing with rypil and process consumer transactions through a payment processor. Nonprofit must be a 501(c)(3) or 501(c)(6) public charity, or other eligible charitable designation, and be in good standing.</p>
    <p><strong>2.2 Eligibility Cap.</strong> To ensure the timely and accurate distribution of contributions, rypil implements the following controls:</p>
    <p><em>A. Suspension for Unresponsiveness.</em> rypil will automatically suspend a nonprofit account if the organization is unresponsive for a period of ninety (90) consecutive days (e.g., failure to provide necessary compliance or payment information).</p>
    <p><em>B. Distribution of Stalled Funds.</em> If remittance to a nonprofit partner cannot occur for 90 consecutive days or if the organization is no longer operating, rypil reserves the right to distribute any remaining balance among other registered nonprofit organizations on the Platform.</p>
  </div>

  <h4 style="margin:8px 0">3. Nature of Role and Relationship</h4>
  <div style="margin-bottom:10px">
    <p><strong>3.1 Independent Parties.</strong> Each Business and NFP acts as an independent entity. Nothing in these Terms constitutes a joint venture, partnership, franchise, or employment relationship.</p>
    <p><strong>3.2 Facilitator Role.</strong> rypil facilitates the connection and distribution of donations but is not a custodian of funds intended for partner nonprofits. rypil controls only the periods and processes of distribution, not the donation amount or timing, which are determined by the business’s consumer activity.</p>
    <p><strong>3.3 Pause or Termination.</strong> Businesses or NFPs may pause, remove, or deactivate participation at any time by submitting a written request through the “Contact Us” section of rypil.com or rypil4biz.com. Deactivation will occur within thirty (30) business days of receipt for Business and nonprofit partners.</p>
  </div>

  <h4 style="margin:8px 0">4. Business Funding and Fees</h4>
  <div style="margin-bottom:10px">
    <p><strong>4.1 Payment/Funding Mechanism.</strong> Businesses fund payment to the rypil platform through a rypil-designated payment processing solution. Nonprofits accept their donations/contributions through a rypil-designated payment processing solution.</p>
    <p><strong>4.2 Business Wallet Credits.</strong> rypil credits the wallet for the full amount funded, less (i) a rypil payment processing fee for services rendered and (ii) the exact third-party processing fees (at cost), which rypil reserves the right to pass directly through to the Business.</p>
    <p><strong>4.3 Transaction Structure.</strong> Each transaction by a registered rypil consumer user at a rypil registered business generates:</p>
    <ul>
      <li>A contribution/donation amount (minimum 2%), configurable per business.</li>
      <li>A platform fee of minimum 1.00% for businesses opted into auto payment or minimum 1.75% for all other payment options.</li>
      <li>A “transaction” means the total purchase amount. For businesses where tipping is routine and customary, the total purchase amount is tip-adjusted.</li>
    </ul>
    <p><strong>4.4 Refunds and Chargebacks.</strong></p>
    <ul>
      <li>Refund period: Reversed, adjusted, or voided transactions &lt; 30 days from the original transaction.</li>
      <li>Full refunds: Contributions are fully reversed only when the payment processor reverses the entire transaction.</li>
      <li>Partial refunds: Contribution recalculated pro-rata.</li>
      <li>Chargebacks: Always honored; contributions reversed based on payment processor outcomes.</li>
      <li>rypil fees: Not subject to any full or partial refund unless the transaction is voided for fraud.</li>
      <li>Refund processing: Adjustments queued for the next payout period for NFPs and within 30 days for businesses.</li>
    </ul>
  </div>

  <h4 style="margin:8px 0">5. Consumer Transaction Flow</h4>
  <div style="margin-bottom:10px">
    <p>When a registered rypil user shops at a rypil registered business, that business makes a contribution/donation to the nonprofit the consumer selected through the rypil platform. Donations/contributions are calculated as a percentage of total purchase amounts (tip-adjusted when applicable) and are facilitated by rypil.</p>
    <p>The donation/contribution is made by the rypil registered business, and neither rypil nor the rypil registered consumer/user is the donor.</p>
  </div>

  <h4 style="margin:8px 0">6. Data and Intellectual Property Rights</h4>
  <div style="margin-bottom:10px">
    <p><strong>6.1 Ownership.</strong> rypil retains ownership of all technology, data, reporting, and intellectual property generated by or through the platform. Businesses and NFPs retain ownership of their existing data and trademarks but grant rypil access to use necessary data solely to perform platform operations.</p>
    <p><strong>6.2 Data Usage and Privacy.</strong> rypil may aggregate, anonymize, and analyze platform data for analytics, reporting, and service improvement. In facilitating the contribution, rypil receives only: (a) business identifier and location, (b) transaction amount, and (c) last four (4) digits of the consumer's payment card. rypil does not receive, process, or store full payment card number, cardholder name, billing address, or other sensitive financial data. Personal or identifiable consumer data is never shared with third parties without explicit consent or as required by law.</p>
    <p><strong>6.3 Data Security.</strong> rypil uses industry-standard encryption and security protocols to protect all data within the platform, including partner administrative data and limited consumer transaction data, from unauthorized access. Payment facilitation partners are solely responsible for PCI DSS compliance of full card and sensitive financial data.</p>
    <p><strong>6.4 Termination of IP License.</strong> On termination or suspension, all trademark/IP licenses immediately terminate. Partner must remove rypil branding/materials within seven (7) days. This provision survives termination.</p>
  </div>

  <h4 style="margin:8px 0">7. Platform Communication and TCPA Compliance</h4>
  <div style="margin-bottom:10px">
    <p><strong>7.1 Administrative Texts.</strong> By registering, the Business/NFP consents to receive administrative text messages from rypil (e.g., security codes, account alerts, and funding updates).</p>
    <p><strong>7.2 Responsibility for Independent Outreach.</strong> If a Business/NFP uses data obtained through rypil to contact a Registered User via SMS outside of the rypil platform, the Business/NFP is solely responsible for obtaining the necessary legal consent.</p>
    <p><strong>7.3 Honoring the “Stop”.</strong> rypil maintains a platform-wide “Master Opt-Out” list. If a user opts out of rypil communications, the Business agrees not to use rypil-provided data to circumvent that choice.</p>
    <p><strong>7.4 Indemnification.</strong> The Business/NFP agrees to indemnify rypil for legal claims arising from unauthorized or non-compliant text communications.</p>
  </div>

  <h4 style="margin:8px 0">8. Dispute Resolution</h4>
  <div style="margin-bottom:10px">
    <p><strong>8.1 Arbitration.</strong> All disputes shall be resolved by binding arbitration administered by AAA under its Commercial Arbitration Rules in Cook County, Illinois, unless mutually agreed otherwise.</p>
    <p><strong>8.2 Class Action Waiver.</strong> All claims must be brought in an individual capacity; no class or collective actions are permitted.</p>
    <p><strong>8.3 Governing Law.</strong> These Terms are governed by Illinois law, without regard to conflict-of-law principles.</p>
  </div>

  <h4 style="margin:8px 0">9. Suspension and Termination</h4>
  <div style="margin-bottom:10px">
    <p>rypil may suspend or terminate any Business or NFP account that violates these Terms, provides inaccurate information, or engages in conduct detrimental to platform integrity. For material breaches (excluding fraud, abuse, or imminent harm), rypil will provide seven (7) days' written notice to cure before suspension or termination. Upon termination, unpaid or pending donations are processed per rypil’s distribution schedule. Section 6.4 obligations apply immediately.</p>
  </div>

  <h4 style="margin:8px 0">10. Limitation of Liability</h4>
  <div style="margin-bottom:10px">
    <p>rypil is not liable for indirect, incidental, or consequential damages arising from participation. Total direct liability shall not exceed total platform fees paid by the business in the six (6) months preceding the claim.</p>
  </div>

  <h4 style="margin:8px 0">11. Amendments</h4>
  <div style="margin-bottom:10px">
    <p>rypil may update these Terms from time to time. Non-material changes take effect upon posting. For material changes (e.g., fees, liability, termination), rypil will notify partner Businesses and Nonprofits via registered email at least 30 days prior to effective date.</p>
  </div>

  <h4 style="margin:8px 0">12. Reporting and Recordkeeping</h4>
  <div style="margin-bottom:10px">
    <p><strong>12.1 Periodic Reporting.</strong> rypil provides periodic reports summarizing contribution/distribution activity, fees, and adjustments.</p>
    <p><strong>12.2 Purpose of Reporting.</strong> Reports support transparency, reconciliation, and compliance with state commercial co-venture laws.</p>
    <p><strong>12.3 Obligation to Review.</strong> Businesses and Nonprofits must review each report and maintain copies for at least three (3) years.</p>
    <p><strong>12.4 Authority for Attestation.</strong> rypil may require electronic acknowledgment and attestation of report accuracy.</p>
  </div>

  <h4 style="margin:8px 0">13. Role-Specific Terms</h4>
  <div style="margin-bottom:10px">
    <p><strong>A. Businesses (Commercial Co-Venturers).</strong> Minimum donation commitment of 2% of each eligible transaction. Beneficiaries are selected by registered rypil users. Businesses must maintain records for three (3) years and consent to required regulatory filings.</p>
    <p><strong>B. Nonprofit.</strong> Must maintain eligible charitable status and use funds for charitable purposes. Nonprofit grants listing/co-marketing rights to rypil per brand guidelines, maintains records for three (3) years, and consents to required filings. Nonprofit is solely responsible for internal parent/chapter apportionments of received contributions.</p>
  </div>

  <h4 style="margin:8px 0">14. Filing Utility and Compliance</h4>
  <div style="margin-bottom:10px">
    <p>This Agreement is intended as a universal Commercial Co-Venture Agreement. rypil may file/register this Agreement and schedules of participating entities in jurisdictions requiring registration/reporting. Each business and nonprofit consents to such filings.</p>
  </div>

  <h4 style="margin:8px 0">Appendix A — Summary of Consumer Donation Flow</h4>
  <div style="margin-bottom:4px">
    <p>When you shop at a rypil registered business as a registered rypil user, that business makes a donation/contribution to the nonprofit you selected through the rypil platform. The donation is made by the business, and neither rypil nor the consumer is the donor.</p>
  </div>

  <div style="margin-top:10px;opacity:0.8;font-size:12px">Version: ${version}</div>
  `);
}
